import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import api from '../../config/axiosConfig';
import { getBaseUrl } from "../../config/api";
import "./NuevaVenta.css";
import { Search, User, CheckCircle } from "lucide-react";
import ModalRepresentantes from "./ModalRepresentantes ";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Preloader from "../../components/Preloader";
import LocationDropdowns from "../../components/LocationDropdowns";
import { DocumentServices } from "../../services/DocumentService";
import { CategoriaService } from "../../services/CategoriaService";
import { RequisitoService } from "../../services/RequisitoService";
import { ProductService } from "../../services/ProductService";
import { ClienteService } from "../../services/ClienteService";
import { SaleService } from "../../services/SaleService";
import UbicacionVenta from "./UbicacionVenta";
import { UsuarioService } from "../../services/UsuarioService";
import Searchable from "../../components/Searchable";
import { LeadService } from "../../services/LeadService";
import { MarcasService } from "../../services/MarcasService";
import { MarcaParametrosService } from "../../services/MarcaParametrosService";

const NuevaVenta = ({
  token,
  user,
  initialData = {},
  readOnly = false,
  onSave,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({
    // Paso 0: Marca
    marca_id: initialData.marca_id || "",

    // Paso 1: Datos adicionales (marca_parametros)
    datosAdicionales: {},

    // Paso 2: Información del Cliente
    codigoCliente: initialData.codigoCliente || "",
    tipoDocumento: initialData.tipoDocumento || "",
    numeroDocumento: initialData.numeroDocumento || "",
    nombres: initialData.nombres || "",
    apellidoPaterno: initialData.apellidoPaterno || "",
    apellidoMaterno: initialData.apellidoMaterno || "",
    telefonoContacto: initialData.telefonoContacto || "",
    telefonoContacto2: initialData.telefonoContacto2 || "",
    nombresCompletos: initialData.nombresCompletos || "",
    vendedor_id: initialData.vendedor_id || "",

    // Paso 3: Datos de ubicación
    departamento: initialData.departamento || "",
    provincia: initialData.provincia || "",
    distrito: initialData.distrito || "",
    zone: initialData.zone || "",
    direccion: initialData.direccion || "",
    direccionReferencia: initialData.direccionReferencia || "",
    correoElectronico: initialData.correoElectronico || "",
    latitud: initialData.latitud || null,
    longitud: initialData.longitud || null,

    // Paso 5: Información del producto
    categoriaProducto: initialData.categoriaProducto || "",
    producto: initialData.producto || "",
    precio: initialData.precio || "",
    telefonoMigrar: initialData.telefonoMigrar || "",
    fechaInstalacion: initialData.fechaInstalacion || "",
    amPm: initialData.amPm || "",

  });

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [idLead, setIdLead] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requisitos, setRequisitos] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [fechaFormateada, setFechaFormateada] = useState("");
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [representantes, setRepresentantes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [mostrarModalRepresentantes, setMostrarModalRepresentantes] =
    useState(false);

  // Estado para las ubicaciones
  const [locationData, setLocationData] = useState({
    department: initialData.department_id || "",
    province: initialData.province_id || "",
    district: initialData.district_id || "",
    zone: initialData.zone_id || "",
  });

  // Estado para paso 0: Marca
  const [marcas, setMarcas] = useState([]);
  const [selectedMarca, setSelectedMarca] = useState(null);
  const [loadingMarcas, setLoadingMarcas] = useState(false);

  // Estado para paso 1: Datos adicionales
  const [marcaParametros, setMarcaParametros] = useState([]);
  const [loadingParametros, setLoadingParametros] = useState(false);

  const handleBackToList = () => {
    navigate("/admin/ventas"); // ajusta según tu ruta de listado
  };

  useEffect(() => {
    if (form.tipoDocumento !== '4') {
      const nombresCompletos = `${form.nombres} ${form.apellidoPaterno} ${form.apellidoMaterno}`.trim();
      setForm(prev => ({ ...prev, nombresCompletos }));
    }
  }, [form.nombres, form.apellidoPaterno, form.apellidoMaterno, form.tipoDocumento]);


  // Función para validar que solo se ingresen números
  const validateNumericInput = (value) => {
    return value.replace(/\D/g, "");
  };

  // Función para manejar cambios en campos numéricos
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const numericValue = validateNumericInput(value);
    setForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const abrirModalRepresentantes = () => setMostrarModalRepresentantes(true);
  const cerrarModalRepresentantes = () => setMostrarModalRepresentantes(false);

  const actualizarRepresentantes = (nuevosRepresentantes) => {
    setRepresentantes(nuevosRepresentantes);
    cerrarModalRepresentantes();
  };

  useEffect(() => {
    if (form.tipoDocumento !== '4') {
      const nombresCompletos = `${form.nombres} ${form.apellidoPaterno} ${form.apellidoMaterno}`.trim();
      setForm(prev => ({ ...prev, nombresCompletos }));
    }
  }, [form.nombres, form.apellidoPaterno, form.apellidoMaterno, form.tipoDocumento]);

  const fetchMarcas = useCallback(async (vendedorId) => {
    if (!vendedorId) return;
    setLoadingMarcas(true);
    try {
      const data = await MarcasService.getMarcasByUserId({ token, userId: vendedorId });
      setMarcas(data);
      setMarcaParametros([]);
    } catch (err) {
      console.error("Error al cargar marcas:", err);
    } finally {
      setLoadingMarcas(false);
    }
  }, [token]);

  const handleSelectMarca = (marca) => {
    setSelectedMarca(marca.id_marca);
    setForm(prev => ({ ...prev, marca_id: marca.id_marca }));
    cargarParametrosMarca(marca.id_marca);
  };

  const cargarParametrosMarca = async (marcaId) => {
    setLoadingParametros(true);
    try {
      const data = await MarcaParametrosService.getByMarcaId({ token, marca_id: marcaId });
      setMarcaParametros(data || []);
    } catch (err) {
      console.error("Error al cargar parámetros de marca:", err);
      setMarcaParametros([]);
    } finally {
      setLoadingParametros(false);
    }
  };

  const handleMarcaParametrosChange = (nombre_campo, value) => {
    setForm(prev => ({
      ...prev,
      datosAdicionales: {
        ...prev.datosAdicionales,
        [nombre_campo]: value
      }
    }));
  };

  useEffect(() => {
    if (productos.length > 0 && form.producto && !form.precio) {
      const seleccionado = productos.find(p => p.id === form.producto);
      if (seleccionado) {
        setForm(prev => ({ ...prev, precio: seleccionado.precio }));
      }
    }
  }, [productos, form.producto, form.precio]);


  useEffect(() => {
    if (requisitos.length > 0) {
      const camposDocumentos = requisitos.reduce((acc, req) => {
        const campo = req.nombre_campo;
        acc[campo] = null;
        acc[`${campo}Preview`] = null;
        return acc;
      }, {});

      setForm((prev) => ({
        ...prev,
        ...camposDocumentos,
      }));
    }
  }, [requisitos]);

  useEffect(() => {
    if (productos.length > 0 && form.producto && !form.precio) {
      const seleccionado = productos.find(p => p.id === form.producto);
      if (seleccionado) {
        setForm(prev => ({ ...prev, precio: seleccionado.precio }));
      }
    }
  }, [productos, form.producto, form.precio]);

  useEffect(() => {
    if (user && !user.permisos?.includes("register_vendores_venta")) {
      fetchMarcas(user.id); 
    }
  }, [user, fetchMarcas]);

  useEffect(() => {
    const actualizarFechaHora = () => {
      const fechaActual = new Date();
      const opcionesFecha = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      setFechaFormateada(
        fechaActual.toLocaleDateString("es-ES", opcionesFecha)
      );

    };

    actualizarFechaHora();

  }, []);

  useEffect(() => {
    if (requisitos.length > 0) {
      const camposDocumentos = requisitos.reduce((acc, req) => {
        const campo = req.nombre_campo;
        acc[campo] = null;
        acc[`${campo}Preview`] = null;
        return acc;
      }, {});

      setForm((prev) => ({
        ...prev,
        ...camposDocumentos,
      }));
    }
  }, [requisitos]);

  const buildLeadData = useCallback((lead) => ({
    ...(lead.id_tipo_documentos && { tipoDocumento: lead.id_tipo_documentos }),
    ...(lead.numero_documento && { numeroDocumento: lead.numero_documento }),
    ...(lead.nombres && { nombres: lead.nombres }),
    ...(lead.ap_paterno && { apellidoPaterno: lead.ap_paterno }),
    ...(lead.ap_materno && { apellidoMaterno: lead.ap_materno }),
    ...(lead.telefono_1 && { telefonoContacto: lead.telefono_1 }),
    ...(lead.telefono_2 && { telefonoContacto2: lead.telefono_2 }),
    ...(lead.email && { correoElectronico: lead.email }),
    ...(lead.direccion && { direccion: lead.direccion }),
    ...(lead.direccion_referencia && { direccionReferencia: lead.direccion_referencia }),
    ...(lead.categoria_id && { categoriaProducto: lead.categoria_id }),
    ...(lead.id_producto_solicita && { producto: lead.id_producto_solicita }),
  }), []);

  const fetchLeadDataByUrl = useCallback(async () => {
    const idLead = searchParams.get('id_lead');
    if (!idLead) return;

    try {
      const responseLead = await LeadService.getById({ token, vid: idLead });
      const lead = responseLead?.lead;
      if (lead?.id === undefined) return;

      setIdLead(idLead);

      if (lead.categoria_id) {
        fetchProductos(lead.categoria_id, form.marca_id);
      }

      const newData = buildLeadData(lead);
      const nombresCompletos = `${lead.nombres || ''} ${lead.ap_paterno || ''} ${lead.ap_materno || ''}`.trim();

      setLocationData((prev) => ({
        ...prev,
        department: String(lead.department_id || '').padStart(2, '0'),
        province: String(lead.province_id || '').padStart(4, '0'),
        district: String(lead.district_id || '').padStart(6, '0'),
        zone: lead.id_region,
      }));

      setForm((prevForm) => ({ ...prevForm, ...newData, nombresCompletos }));
    } catch (error) {
      console.error("Error cargando el lead:", error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token, form.marca_id, buildLeadData]);

  const fetchCategorias = useCallback(async () => {
    setLoadingCategorias(true);
    try {
      const data = await CategoriaService.getCategorias(token);
      setCategorias(data);
    } catch (err) {
      console.error("Error al cargar categorías:", err);
    } finally {
      setLoadingCategorias(false);
    }
  }, [token]);

  const fetchVendedores = useCallback(async () => {
    setLoading(true);
    try {
      const data = await UsuarioService.fetchVendedores(token);
      setVendedores(data);
    } catch (err) {
      console.error("Error al cargar categorías:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const cargarTipoDocumentos = useCallback(async () => {
    try {
      const tipos = await DocumentServices.getDocuments(token);
      setTiposDocumentos(tipos || []);
    } catch (error) {
      console.error("Error al cargar tipos de Documentos:", error);
    }
  }, [token]);

  const fetchRequisitos = useCallback(async () => {
    try {
      const data = await RequisitoService.fetchRequisitos(token);
      setRequisitos(data);
    } catch (err) {
      console.error("Error al cargar requisitos:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchCategorias();
    fetchRequisitos();
    fetchVendedores();
    cargarTipoDocumentos();
    if (searchParams.has('id_lead')) {
      fetchLeadDataByUrl();
    }
  }, [user, searchParams, fetchCategorias, fetchRequisitos, fetchVendedores, cargarTipoDocumentos, fetchLeadDataByUrl]);

  const fetchProductos = useCallback(async (value, marcaId = null) => {
    if (!value) return;
    setLoading(true);
    try {
      const productos = await ProductService.fetchProductosPorCategoria(token, value, marcaId);
      setProductos(productos || []);
    } catch (err) {
      console.error("Error al cargar productos:", err);
      setError("Error al cargar los productos.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === "vendedor_id") {
      setForm(prev => ({ ...prev, vendedor_id: value, categoriaProducto: "", producto: "", marca_id: "" }));
      setMarcaParametros([]);
      fetchMarcas(value);
      return;
    }
    if (name === "categoriaProducto") {
      fetchProductos(value, form.marca_id);
      setForm((prev) => ({ ...prev, producto: "", precio: "" }));
    }

    if (name === 'tipoDocumento') {
      setRepresentantes([]);
      if (idLead === 0) {
        setForm(prev => ({
          ...prev,
          nombres: "",
          apellidoPaterno: "",
          apellidoMaterno: "",
          nombresCompletos: "",
        }));
      }
    }

    if (name === "producto") {
      const productoSeleccionado = productos.find(p => p.id === Number(value));

      if (productoSeleccionado) {
        setForm((prev) => ({
          ...prev,
          precio: productoSeleccionado.precio,
        }));
      }
    }
  };

  const handleFileChange = (fieldName, file) => {
    const MAX_FILE_SIZE_MB = 2;
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"];

    if (file) {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileType = file.type;

      if (!ALLOWED_TYPES.includes(fileType)) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: "Solo se permiten imágenes o archivos PDF",
        }));
        return;
      }

      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: "El archivo no debe superar los 2 MB",
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setForm({
          ...form,
          [fieldName]: file,
          [`${fieldName}Preview`]: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setForm({
        ...form,
        [fieldName]: null,
        [`${fieldName}Preview`]: null,
      });
      setErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 6) {
      let next = currentStep + 1;
      if (marcaParametros.length === 0 && (currentStep === 0 || currentStep === 1)) {
        next = 2;
      }
      setCurrentStep(next);
      setError("");
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      let prev = currentStep - 1;
      if (currentStep === 2 && marcaParametros.length === 0) {
        prev = 0;
      }
      setCurrentStep(prev);
      setError("");
    }
  };

  const buscarCliente = async () => {
    try {
      if (!form.numeroDocumento || form.numeroDocumento.trim() === "") {
        setErrors((prev) => ({
          ...prev,
          numeroDocumento: "Ingrese un número válido",
        }));
        return;
      }

      const cliente = await ClienteService.buscarPorDocumento({
        token,
        tipoDocumento: form.tipoDocumento,
        numeroDocumento: form.numeroDocumento,
      });

      if (form.tipoDocumento === "1" && cliente) {
        setForm((prev) => ({
          ...prev,
          nombres: cliente.nombres || "",
          apellidoMaterno: cliente.apellido_materno || "",
          apellidoPaterno: cliente.apellido_paterno || "",
          nombresCompletos: cliente.nombres + " " + cliente.apellido_paterno + " " + cliente.apellido_materno
        }));
      }

      if (form.tipoDocumento === "4" && cliente) {
        setForm((prev) => ({
          ...prev,
          nombresCompletos: cliente.nombre_o_razon_social || "",
          direccion: cliente.direccion || "",
          departamento: cliente.ubigeo?.[0] || "",
          provincia: cliente.ubigeo?.[1] || "",
          distrito: cliente.ubigeo?.[2] || "",
        }));

        setLocationData((prev) => ({
          ...prev,
          department: cliente.ubigeo?.[0] || "",
          province: cliente.ubigeo?.[1] || "",
          district: cliente.ubigeo?.[2] || "",
          zone: "",
        }));
      }

      setErrors((prev) => ({
        ...prev,
        numeroDocumento: null,
      }));
    } catch (err) {
      const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
      setErrors((prev) => ({
        ...prev,
        numeroDocumento: mensaje,
      }));
    }
  }

  const validarCodigoCliente = async (codigo) => {
    try {
      const existe = await SaleService.validarCodigo(token, codigo);

      setErrors((prev) => ({
        ...prev,
        codigoCliente: existe ? "Este código ya está registrado" : null,
      }));
    } catch (error) {
      console.error("Error al validar código:", error);
      setErrors((prev) => ({
        ...prev,
        codigoCliente: "Error al validar el código",
      }));
    }
  };

  const validateStep = async () => {
    const errs = {};
    const r = (field, key, msg) => { if (!field) errs[key] = msg; };

    const validators = {
      0: () => { if (!form.marca_id && !selectedMarca) errs.marca_id = "Debe seleccionar una marca"; },
      1: () => {
        marcaParametros.forEach(param => {
          if (param.requerido === 1) {
            const value = form.datosAdicionales?.[param.nombre_campo];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              errs[param.nombre_campo] = `${param.nombre_campo} es obligatorio`;
            }
          }
        });
      },
      3: () => {
        r(locationData.department, "department", "Departamento es obligatorio");
        r(locationData.province, "province", "Provincia es obligatoria");
        r(locationData.district, "district", "Distrito es obligatorio");
        r(form.direccion, "direccion", "Dirección es obligatoria");
      },
      4: () => { if (form.latitud === null || form.longitud === null) errs.ubicacion = 'Debe seleccionar una ubicación en el mapa'; },
      5: () => {
        r(form.categoriaProducto, "categoriaProducto", "Categoría es obligatoria");
        r(form.producto, "producto", "Producto es obligatorio");
        r(form.precio, "precio", "Precio es obligatorio");
      },
      6: () => {
        requisitos.forEach((req) => {
          if (req.obligatorio === "1" && req.nombre_campo && !form[req.nombre_campo] && !form[`${req.nombre_campo}Preview`]) {
            errs[req.nombre_campo] = `${req.descripcion} es obligatorio.`;
          }
        });
      },
    };

    const validateStep2 = async () => {
      r(form.tipoDocumento, "tipoDocumento", "Tipo de documento es obligatorio");
      r(form.numeroDocumento, "numeroDocumento", "Número de documento es obligatorio");
      r(form.telefonoContacto, "telefonoContacto", "Teléfono de contacto es obligatorio");
      if (form.tipoDocumento !== '4') {
        r(form.nombres, "nombres", "Nombres son obligatorios");
        r(form.apellidoPaterno, "apellidoPaterno", "Apellido paterno es obligatorio");
      } else if (!form.nombresCompletos) errs.nombresCompletos = "Razón Social es obligatorios";
      if (user?.permisos?.includes("register_vendores_venta") && !form.vendedor_id) errs.vendedor_id = "Debe seleccionar un vendedor";
      if (form.codigoCliente?.trim()) {
        const resultado = await SaleService.validarCodigo(token, form.codigoCliente);
        if (resultado.existe) errs.codigoCliente = 'El código ya existe';
      }
    };

    if (validators[currentStep]) validators[currentStep]();
    if (currentStep === 2) await validateStep2();

    return errs;
  };

  const buildFormData = (coords) => {
    const fd = new FormData();
    fd.append("id_marca", form.marca_id);
    fd.append("producto", form.producto);
    fd.append("monto", Number.parseFloat(form.precio) || 0);
    fd.append("codigoCliente", form.codigoCliente);
    fd.append("tipoDocumento", form.tipoDocumento);
    fd.append("numeroDocumento", form.numeroDocumento);
    fd.append("nombres", form.nombres);
    fd.append("idLead", idLead);
    fd.append("apellidoPaterno", form.apellidoPaterno);
    fd.append("apellidoMaterno", form.apellidoMaterno);
    fd.append("telefonoContacto", form.telefonoContacto);
    fd.append("telefonoContacto2", form.telefonoContacto2);
    fd.append("nombresCompletos", form.nombresCompletos);
    fd.append("representantes", JSON.stringify(representantes));
    fd.append("vendedor_id", form.vendedor_id);
    fd.append("departamento", locationData.department || form.departamento);
    fd.append("provincia", locationData.province || form.provincia);
    fd.append("distrito", locationData.district || form.distrito);
    fd.append("zone", locationData.zone || form.zone);
    fd.append("direccion", form.direccion);
    fd.append("direccionReferencia", form.direccionReferencia);
    fd.append("correoElectronico", form.correoElectronico);
    fd.append("latitud_cliente", String(form.latitud));
    fd.append("longitud_cliente", String(form.longitud));
    if (coords.latitude && coords.longitude) {
      fd.append("latitud", coords.latitude.toString());
      fd.append("longitud", coords.longitude.toString());
    }
    fd.append("ventaDetalleId", form.ventaDetalleId);
    fd.append("categoriaProducto", form.categoriaProducto);
    fd.append("precio", form.precio);
    fd.append("telefonoMigrar", form.telefonoMigrar);
    fd.append("fechaInstalacion", form.fechaInstalacion);
    fd.append("amPm", form.amPm);
    fd.append("origen", 'web');
    if (Object.keys(form.datosAdicionales || {}).length > 0) {
      fd.append("datosAdicionales", JSON.stringify(form.datosAdicionales));
    }
    requisitos.forEach((req) => {
      if (form[req.nombre_campo] instanceof File) fd.append(req.nombre_campo, form[req.nombre_campo]);
    });
    return fd;
  };

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocalización no soportada"));
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position.coords),
            (error) => reject(new Error(error.message || "Error de geolocalización")),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    const newErrors = await validateStep();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (currentStep < 6) {
      nextStep();
      return;
    }

    setLoading(true);
    try {
      let coords = {};
      try {
        coords = await getLocation();
      } catch (geoError) {
        console.warn("No se pudo obtener ubicación:", geoError.message);
      }

      const formData = buildFormData(coords);

      const response = await api.post("/ventas", formData, {
        headers: { Authorization: `Bearer ${token}` },
        "Content-Type": "multipart/form-data",
      });

      if (response.data.success) {
        toast.success(response.data.message || "Venta registrada correctamente");
        navigate("/admin/ventas");
      } else {
        toast.error(response.data.message || "Error al registrar la venta");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al registrar la venta: " + (error.message || ""), {
        position: "top-center", autoClose: 3000, theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep2 = () => (
    <div className="form-section">
      <h3 className="section-title">Datos del Cliente</h3>

      <div className="form-grid">
        <div className="">
          <label htmlFor="codigoCliente">Código de Cliente</label>
          <input
            type="text"
            name="codigoCliente"
            value={form.codigoCliente}
            onChange={(e) => {
              handleChange(e);
              validarCodigoCliente(e.target.value); // o usar onBlur si prefieres
            }}
            disabled={readOnly}
            placeholder="Código del cliente"
          />
          {errors.codigoCliente && (
            <span className="error">{errors.codigoCliente}</span>
          )}
        </div>
        <div className="">
          <label htmlFor="tipoDocumento">Tipo de Documento *</label>
          <select
            name="tipoDocumento"
            value={form.tipoDocumento}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">Selecciona tipo de documento</option>
            {tiposDocumentos.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.description}
              </option>
            ))}
          </select>
          {errors.tipoDocumento && (
            <span className="error">{errors.tipoDocumento}</span>
          )}
        </div>
      </div>

      <div className="form-grid">
        <div className="">
          <label htmlFor="numeroDocumento">Número de Documento *</label>
          <div className="input-with-button">
            <input
              type="text"
              name="numeroDocumento"
              value={form.numeroDocumento}
              onChange={handleNumericChange}
              disabled={readOnly}
              placeholder="Solo números"
            />
            {(form.tipoDocumento === "1" || form.tipoDocumento === "4") && (
              <button
                type="button"
                className="search-btn"
                title="Buscar cliente"
                onClick={buscarCliente}
              >
                <Search size={20} color="#ffffff" />
              </button>
            )}
          </div>
          {errors.numeroDocumento && (
            <span className="error">{errors.numeroDocumento}</span>
          )}
        </div>
        <div className="">
          <label htmlFor="nombres">Nombres</label>
          <input
            type="text"
            name="nombres"
            value={form.nombres}
            onChange={handleChange}
            disabled={readOnly}
            readOnly={form.tipoDocumento === '4'}
            placeholder="Nombres del cliente"
          />
          {errors.nombres && <span className="error">{errors.nombres}</span>}
        </div>
      </div>

      <div className="form-grid">
        <div className="">
          <label htmlFor="apellidoPaterno">Apellido Paterno </label>
          <input
            type="text"
            name="apellidoPaterno"
            value={form.apellidoPaterno}
            onChange={handleChange}
            disabled={readOnly}
            readOnly={form.tipoDocumento === '4'}
            placeholder="Apellido paterno"
          />
          {errors.apellidoPaterno && (
            <span className="error">{errors.apellidoPaterno}</span>
          )}
        </div>
        <div className="">
          <label htmlFor="apellidoMaterno">Apellido Materno</label>
          <input
            type="text"
            name="apellidoMaterno"
            value={form.apellidoMaterno}
            onChange={handleChange}
            disabled={readOnly}
            readOnly={form.tipoDocumento === '4'}
            placeholder="Apellido materno"
          />
          {errors.apellidoMaterno && (
            <span className="error">{errors.apellidoMaterno}</span>
          )}
        </div>
      </div>

      <div className="form-grid">
        <div className="">
          <label htmlFor="telefonoContacto">Teléfono de Contacto *</label>
          <input
            type="tel"
            name="telefonoContacto"
            value={form.telefonoContacto}
            onChange={handleNumericChange}
            disabled={readOnly}
            placeholder="Solo números"
          />
          {errors.telefonoContacto && (
            <span className="error">{errors.telefonoContacto}</span>
          )}
        </div>
        <div className="">
          <label htmlFor="nombresCompletos">Nombres Completos / Razón Social</label>
          <div className="input-with-button">
            <input
              type="text"
              name="nombresCompletos"
              value={form.nombresCompletos}
              onChange={handleChange}
              disabled={readOnly}
              readOnly={form.tipoDocumento !== '4'}
              placeholder="Nombres completos - Razón social..."
            />
            {form.tipoDocumento === "4" && (
              <button
                type="button"
                className="contact-btn"
                title="Seleccionar contacto"
                onClick={abrirModalRepresentantes}
              >
                <User size={24} color="white" />
              </button>
            )}
          </div>
          {mostrarModalRepresentantes && (
            <ModalRepresentantes
              clientesSeleccionados={representantes}
              onGuardar={actualizarRepresentantes}
              onCerrar={cerrarModalRepresentantes}
              numero={form.numeroDocumento}
              tipoDocumento={form.tipoDocumento}
              token={token}
            />
          )}
          {errors.nombresCompletos && (
            <span className="error">{errors.nombresCompletos}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    return (
      <div className="form-section">
        <h3 className="section-title"> Dirección del cliente</h3>

        <LocationDropdowns
          selectedDepartment={locationData.department}
          selectedProvince={locationData.province}
          selectedDistrict={locationData.district}
          selectedZone={locationData.zone}
          errors={errors}
          onDepartmentChange={(value) => {
            setLocationData((prev) => ({
              ...prev,
              department: value,
              province: "",
              district: "",
              zone: "",
            }));
            // Sincronizar con form
            setForm((prev) => ({
              ...prev,
              departamento: value,
              provincia: "",
              distrito: "",
              zone: "",
            }));
          }}
          onProvinceChange={(value) => {
            setLocationData((prev) => ({
              ...prev,
              province: value,
              district: "",
              zone: "",
            }));
            // Sincronizar con form
            setForm((prev) => ({
              ...prev,
              provincia: value,
              distrito: "",
              zone: "",
            }));
          }}
          onDistrictChange={(value) => {
            setLocationData((prev) => ({
              ...prev,
              district: value,
              zone: "",
            }));
            // Sincronizar con form
            setForm((prev) => ({
              ...prev,
              distrito: value,
              zone: "",
            }));
          }}
          onZoneChange={(value) => {
            setLocationData((prev) => ({
              ...prev,
              zone: value,
            }));
            // Sincronizar con form
            setForm((prev) => ({
              ...prev,
              zone: value,
            }));
          }}
          showZones={true}
          className="location-dropdowns"
          token={token}
        />

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="correoElectronico">Correo Electrónico</label>
            <input
              type="email"
              name="correoElectronico"
              value={form.correoElectronico}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="Correo electrónico"
            />
            {errors.correoElectronico && (
              <span className="error">{errors.correoElectronico}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="telefonoContacto2">Teléfono de Contacto (Opcional)</label>
            <input
              type="tel"
              name="telefonoContacto2"
              value={form.telefonoContacto2}
              onChange={handleNumericChange}
              disabled={readOnly}
              placeholder="Solo números (opcional)"
            />
            {errors.telefonoContacto2 && (
              <span className="error">{errors.telefonoContacto2}</span>
            )}
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="direccion">Dirección *</label>
            <textarea
              name="direccion"
              value={form.direccion}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="Dirección"
              rows={3}
              style={{ resize: "vertical" }}
            />
            {errors.direccion && (
              <span className="error">{errors.direccion}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="direccionReferencia">Dirección de Referencia</label>
            <textarea
              name="direccionReferencia"
              value={form.direccionReferencia}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="Dirección de referencia"
              rows={3}
              style={{ resize: "vertical" }}
            />
            {errors.direccionReferencia && (
              <span className="error">{errors.direccionReferencia}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    return (
      <div className="form-section">
        <h3 className="section-title">Ubicación en el mapa</h3>
        <p>Verifica la ubicación aproximada del cliente en el mapa:</p>
        <UbicacionVenta form={form} setForm={setForm} />
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="form-section">
      <h3 className="section-title">Datos del Producto</h3>

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="categoriaProducto">Categoría Producto *</label>
          <select
            name="categoriaProducto"
            value={form.categoriaProducto}
            onChange={handleChange}
            disabled={readOnly || loadingCategorias}
            className="form-select"
          >
            <option value="">Seleccionar categoría</option>
            {categorias.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nombre}
              </option>
            ))}
          </select>
          {errors.categoriaProducto && (
            <span className="error">{errors.categoriaProducto}</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="producto">Producto *</label>
          <select
            name="producto"
            value={form.producto}
            onChange={handleChange}
            disabled={readOnly || !form.categoriaProducto}
            className="form-select"
          >
            <option value="">Seleccionar producto</option>
            {productos.map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.nombre} - S/ {producto.precio}
              </option>
            ))}
          </select>
          {errors.producto && <span className="error">{errors.producto}</span>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="precio">Precio *</label>
          <input
            type="number"
            name="precio"
            value={form.precio}
            onChange={handleChange}
            disabled={readOnly}
            readOnly={true}
            step="0.01"
            placeholder="Precio"
          />
          {errors.precio && <span className="error">{errors.precio}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="telefonoMigrar">Teléfono a Migrar</label>
          <input
            type="tel"
            name="telefonoMigrar"
            value={form.telefonoMigrar}
            onChange={handleNumericChange}
            disabled={readOnly}
            placeholder="Solo números"
          />
          {errors.telefonoMigrar && (
            <span className="error">{errors.telefonoMigrar}</span>
          )}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="fechaInstalacion">Fecha de Instalación</label>
          <input
            type="datetime-local"
            name="fechaInstalacion"
            value={form.fechaInstalacion}
            onChange={handleChange}
          />
          {errors.fechaInstalacion && (
            <span className="error">{errors.fechaInstalacion}</span>
          )}
        </div>
        {/* <div className="form-group">
          <label>AM/PM</label>
          <select
            name="amPm"
            value={form.amPm}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">Seleccionar</option>
            {opcionesAmPm.map((opcion) => (
              <option key={opcion.value} value={opcion.value}>
                {opcion.label}
              </option>
            ))}
          </select>
          {errors.amPm && <span className="error">{errors.amPm}</span>}
        </div> */}
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="form-section">
      <h3 className="section-title">📷 Documentos Requeridos</h3>

      <div className="document-grid">
        {requisitos.map((req) => {
          const fieldName = req.nombre_campo;
          const previewField = `${fieldName}Preview`;
          const isRequired = req.obligatorio === "1";
          const preview = form?.[previewField];

          return (
            <div className="document-card" key={req.id}>
              {preview ? (
                <div className="image-preview">
                  <img src={preview} alt={req.descripcion} />
                  <button
                    type="button"
                    className="remove-image"
                    onClick={() => handleFileChange(fieldName, null)}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="document-icon">📷</div>
              )}

              <label>
                {req.descripcion} {isRequired && "*"}
              </label>

              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) =>
                  handleFileChange(fieldName, e.target.files[0])
                }
                disabled={readOnly}
              />

              {errors?.[fieldName] && (
                <span className="error">{errors[fieldName]}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep0 = () => (
    <div className="form-section">
      <h3 className="section-title">Seleccionar Marca</h3>
      <p>Obligatorio: Seleccione una marca para continuar.</p>
      {loadingMarcas ? (
        <div className="loading">Cargando marcas...</div>
      ) : (
        <div className="marcas-grid">
          {(marcas || []).map((marca, index) => (
            <button
              type="button"
             key={marca.id_marca ? `marca-${marca.id_marca}` : `marca-idx-${index}`}
              className={`marca-card ${selectedMarca === marca.id_marca ? 'selected' : ''}`}
              onClick={() => handleSelectMarca(marca)}
            >
              {marca.imagen ? (
                <img src={`${getBaseUrl()}/uploads/${marca.imagen}`} alt={marca.descripcion} />
              ) : (
                <div className="marca-placeholder">
                  <span>{marca.descripcion?.charAt(0) || "?"}</span>
                </div>
              )}
              <div className="marca-card-label">{marca.descripcion}</div>
              {selectedMarca === marca.id_marca && (
                <div className="marca-card-check">
                  <CheckCircle size={24} color="#fff" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {errors.marca_id && (
        <span className="error">{errors.marca_id}</span>
      )}
    </div>
  );

  const renderStep1 = () => (
    <div className="form-section">
      <h3 className="section-title">Datos Adicionales</h3>

      {loadingParametros ? (
        <div className="loading">Cargando parámetros...</div>
      ) : (
        <div className="form-grid">
          {marcaParametros.map((param) => {
            const fieldName = param.nombre_campo;
            const fieldValue = form.datosAdicionales?.[fieldName] || "";
            const handleChange = (e) => handleMarcaParametrosChange(fieldName, e.target.value);

            let inputControl;
            if (param.tipo_campo === "selector" && param.valores_selector) {
              inputControl = (
                <select name={fieldName} value={fieldValue} onChange={handleChange}>
                  <option value="">Seleccionar...</option>
                  {JSON.parse(param.valores_selector).map((val, idx) => (
                    <option key={`${param.id}-${val}`} value={val}>{val}</option>
                  ))}
                </select>
              );
            } else if (param.tipo_campo === "fecha") {
              inputControl = (
                <input type="date" name={fieldName} value={fieldValue} onChange={handleChange} />
              );
            } else {
              inputControl = (
                <input type={param.tipo_campo === "number" ? "number" : "text"} name={fieldName}
                  value={fieldValue} onChange={handleChange} placeholder={fieldName} />
              );
            }

            return (
              <div className="form-group" key={param.id}>
                <label>{fieldName} {param.requerido === 1 ? "*" : ""}</label>
                {inputControl}
                {errors[fieldName] && <span className="error">{errors[fieldName]}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep0();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      default:
        return renderStep0();
    }
  };

  let buttonText = "Siguiente";
  if (loading) buttonText = "Procesando...";
  else if (currentStep === 6) buttonText = "Registrar Venta";

  return (
    <div className="ventas-admin-content">
      {loading && <Preloader />}
      <div className="breadcrumb-container">
        <button type="button" className="breadcrumb-link" onClick={handleBackToList} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--color-primary)", textDecoration: "underline", font: "inherit" }}>
          ← Listado de Ventas
        </button>
        <span className="breadcrumb-current"> / Registrar Venta</span>
      </div>
      <div className="dashboard-card nuevo-producto-card">
        <span className="nueva-venta-title"> Nueva Venta</span>
        {user?.permisos?.includes("register_vendores_venta") ? (
          <span className="nueva-venta-subtitle">
            <div className="selector-vendedor">
              <label htmlFor="vendedor">Seleccionar vendedor:</label>

              <Searchable
                options={vendedores}
                value={form.vendedor_id}
                onChange={(id) => {
                  handleChange({
                    target: {
                      name: "vendedor_id",
                      value: id
                    }
                  });
                }}
                placeholder="Seleccione vendedor..."
                getOptionLabel={(v) => `${v.nombre} ${v.apellidoPaterno}`}
                getOptionValue={(v) => v.id}
              />
              {errors.vendedor_id && (
                <span className="error">{errors.vendedor_id}</span>
              )}
            </div>
          </span>
        ) : (
          <span className="nueva-venta-subtitle">
            {user.perfil_nombre} - {user.nombre + " " + user.apellidoPaterno}
          </span>
        )}
        <span className="nueva-venta-subtitle">
          {fechaFormateada}
        </span>

        <form
          className="nueva-venta-form"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {renderCurrentStep()}

          {error && <div className="error-message">{error}</div>}
          

          {!readOnly && (
            <div className="form-actions">
              {currentStep > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={prevStep}
                  disabled={loading}
                >
                  Anterior
                </button>
              )}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading || (currentStep === 0 && loadingParametros)}
              >
                {buttonText}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

NuevaVenta.propTypes = {
  token: PropTypes.string,
  user: PropTypes.object,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
};

export default NuevaVenta;
